import { useCallback, useEffect, useMemo, useState } from "react";
import { AccountPresenter } from "./AccountPresenter";
import * as s from "./AccountStyled";
import TableButton from "components/atoms/buttons/TableButton";
import { useQuery } from "react-query";
import { getAccountTreeList } from "api/accountAPI";
import {
  AccountInfo,
  ProfileAccountInfo,
  ProfileAccountInfoFromServer,
} from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { changeLevelFromABBToABB, formatNumber } from "utils/functions";
import { useLocation } from "react-router-dom";
import {
  BasePagination,
  LEVEL_TYPE,
  PageTotalStatus,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { isNull } from "lodash";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { Popover, TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import { checkIsSuspended } from "utils/AccountUtil";
import * as mediaQuery from "components/MediaQuery";
import SuspendedLabel from "components/atoms/text/labels/SuspendedLabel";
import AccountId from "components/atoms/text/labels/AccountId";
import { SummaryCardRecorder } from "./card/SummaryCardRecorder";
import { SummaryCardCamera } from "./card/SummaryCardCamera";
import { ExpandableConfig } from "antd/es/table/interface";
import AccountExpandContainer from "./AccountExpandContainer";
import { MdOutlineExpand } from "react-icons/md";
import { BsArrowsCollapse } from "react-icons/bs";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { PopoverWrapper } from "./card/SummaryCardStyled";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { suspendOptions } from "utils/options";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import useAccountAccess from "hook/useAccountAccess";

const initialQuery: BasePagination = baseQueryInfo;
export function AccountContainer(): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const location: any = useLocation();
  const { queryNavigate } = useMenu({});

  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<null | AccountInfo>(null);
  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();
  const [status, setStatus] = useState<PageTotalStatus>({
    totalTitle: "Total",
    totalValue: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setAccountQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "name"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.ASC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setAccountQueryInfo({ ...initialQuery });
    }
  }, [location]);

  const onModal = (
    type?: string,
    row?: AccountInfo | ProfileAccountInfoFromServer | string
  ) => {
    if (type === "add") {
      setIsEditModal(null);
      setIsAddModal(!isAddModal);
    }
    if (type === "edit") {
      setIsAddModal(false);
      setIsEditModal((row as AccountInfo) ?? null);
    }
  };

  const rowEnabled = useCallback((row: AccountInfo) => {
    return (
      checkIsSuspended({
        ...row,
        accountLevel: row.level as LEVEL_TYPE,
      }) !== SUSPENSION_STATUS.ENABLED
    );
  }, []);

  const renderEnableRow = useCallback(
    (row: AccountInfo, value: number, PopoverComponent?: React.ReactNode) => {
      if (rowEnabled(row)) {
        return `-`;
      }
      if (PopoverComponent && value > 0) {
        return (
          <PopoverWrapper>
            <Popover
              trigger="click"
              placement="bottom"
              content={PopoverComponent}
            >
              <s.UnderlineButton>{formatNumber(value)}</s.UnderlineButton>
            </Popover>
          </PopoverWrapper>
        );
      }
      return formatNumber(value);
    },
    []
  );

  const [accountQueryInfo, setAccountQueryInfo] = useState<BasePagination>({
    ...initialQuery,
  });
  const queryKey = useQueryKey(accountQueryInfo);
  const isRowExpandable = useCallback(
    (record: AccountInfo) =>
      record.hasAccounts > 0 && record.level !== LEVEL_TYPE.EU,
    []
  );

  const { data, refetch, isLoading } = useQuery(
    ["accountTreeList", queryKey, selectedAccount.accountId],
    () =>
      getAccountTreeList({
        payload: accountQueryInfo,
        accountId: selectedAccount.accountId,
        isSelf: false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        setStatus({
          ...status,
          totalValue: res.page.total,
        });
        if (res.result === undefined) {
          setAccountQueryInfo((info) => {
            return { ...info, ...initialQuery };
          });
        } else {
          setAccountQueryInfo((info) => {
            return { ...info, ...res.page };
          });
        }
      },
      onError: (e: any) => {
        setStatus({
          totalTitle: "Total ",
          totalValue: 0,
        });

        setAccountQueryInfo({ ...initialQuery });
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: [
      "organizationListUpdated",
      "loginAccessUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  const accountList = useMemo((): AccountInfo[] => {
    if (data && data.result) {
      return data.result.map((account: AccountInfo) => {
        return {
          ...account,
          key: account.accountId,
        };
      });
    }
    return [];
  }, [data]);

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (accountQueryInfo.keyword !== keyword) {
        queryNavigate({
          to: `/account`,
          queryInfo: {
            ...accountQueryInfo,
            pageNum: 0,
            keyword: keyword,
          },
        });
      } else {
        refetch();
      }
    },
    [accountQueryInfo, queryNavigate, refetch]
  );

  const onChangePage = (page: number, totalRows: number) => {
    // console.log(page, totalRows);
    if (page !== accountQueryInfo.pageNum + 1) {
      queryNavigate({
        to: `/account`,
        queryInfo: {
          ...accountQueryInfo,
          pageNum: page - 1,
        },
      });
      // setAccountQueryInfo((info) => {
      //   return { ...info, pageNum: (page - 1) as number };
      // });
    }
  };

  const onHeaderCell = (column: ColumnType<AccountInfo>) => {
    return {
      onClick: () => {
        if (column.key) {
          setAccountQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const onClickFilterTitle = useCallback(
    (value: string | undefined, name: "status") => {
      if (value !== undefined) {
        if (value === "all") {
          if (accountQueryInfo[name] !== undefined) {
            setAccountQueryInfo((info) => {
              return { ...info, pageNum: 0, [name]: "" };
            });
          }
        } else {
          if (accountQueryInfo[name] !== value) {
            setAccountQueryInfo((info) => {
              return { ...info, pageNum: 0, [name]: value };
            });
          }
        }
      }
    },
    [accountQueryInfo]
  );

  const onSuccessCreateAccount = () => {
    setIsAddModal(false);
    refetch();
    queryNavigate({
      to: `/account`,
      queryInfo: {
        ...accountQueryInfo,
      },
    });
  };

  const expandableRowKeysInPage = useMemo(() => {
    return (accountList ?? [])
      .filter(isRowExpandable)
      .map((row) => row.accountId);
  }, [accountList, isRowExpandable]);

  const isAllExpandedInPage = useMemo(() => {
    if (expandableRowKeysInPage.length === 0) return false;

    // 페이지에서 펼칠 수 있는 것들이 모두 expandedRowKeys에 포함되어 있으면 true
    return expandableRowKeysInPage.every((key) =>
      expandedRowKeys.includes(key)
    );
  }, [expandableRowKeysInPage, expandedRowKeys]);

  const onToggleExpandAllInPage = useCallback(() => {
    setExpandedRowKeys((prev) => {
      if (expandableRowKeysInPage.length === 0) return prev;

      // 이미 전부 펼쳐져 있으면 접기(해당 페이지 것만 제거)
      if (expandableRowKeysInPage.every((key) => prev.includes(key))) {
        return prev.filter((key) => !expandableRowKeysInPage.includes(key));
      }

      // 아니면 펼치기(기존 + 페이지 key 합집합)
      const merged = new Set([...prev, ...expandableRowKeysInPage]);
      return Array.from(merged);
    });
  }, [expandableRowKeysInPage]);

  const { AccountAccessButton, AccountAccessModal } = useAccountAccess({
    buttonType: "Login As",
    useTemporary: true,
    isShowOnlyUser: false,
  });

  const columns: TableColumnsType<AccountInfo> = [
    {
      key: "accountNumber",
      dataIndex: "accountNumber",
      title: "ID",
      render: (value, row) => {
        return (
          <AccountId
            accountId={value}
            level={changeLevelFromABBToABB(row) as LEVEL_TYPE}
          />
        );
      },
      width: 95,
      sorter: true,
      sortOrder: changeSortOrderAntd(accountQueryInfo, "accountNumber"),
      onHeaderCell: onHeaderCell,
      hidden: isMobile,
    },
    {
      key: "name",
      title: "Name",
      render: (value, row) => (
        <>
          {isMobile && (
            <AccountId
              accountId={row.accountNumber}
              level={changeLevelFromABBToABB(row) as LEVEL_TYPE}
            />
          )}

          <s.NameLabel>{row.name}</s.NameLabel>
        </>
      ),

      sorter: true,
      sortOrder: changeSortOrderAntd(accountQueryInfo, "name"),
      defaultSortOrder: "ascend",
      onHeaderCell: onHeaderCell,
      ellipsis: true,
    },

    {
      title: (
        <TableColumnFilterTitle
          title="Status"
          filters={suspendOptions.map((opt) => {
            return { label: opt.label, value: opt.label.toLowerCase() };
          })}
          onClick={(value: string | undefined) => {
            onClickFilterTitle(value, "status");
          }}
          data-testid="account-account-container-tsx-account-table-column-filter-title-click-filter-title"
        />
      ),

      render: (value, row) => {
        const suspendedStatus = checkIsSuspended({
          ...row,
          accountLevel: row.level as LEVEL_TYPE,
        });
        return <SuspendedLabel status={suspendedStatus} />;
      },
      width: 80,
      hidden: isMobile,
    },
    {
      title: "Sub Organizations",
      render: (value, row) =>
        row.level === LEVEL_TYPE.EU || rowEnabled(row)
          ? `-`
          : formatNumber(value),
      align: "center",
      responsive: ["md"],
      width: "10%",
      key: "hasAccounts",
      dataIndex: "hasAccounts",
    },
    {
      title: "Users",
      dataIndex: "hasUsers",
      key: "hasUsers",
      render: (value, row) => renderEnableRow(row, value),
      align: "center",
      responsive: ["lg"],
      width: "8%",
    },
    {
      title: "Recorders",
      key: "hasRecorders",
      dataIndex: "hasRecorders",
      render: (value, row) =>
        renderEnableRow(
          row,
          value,
          <SummaryCardRecorder accountId={row.accountId} />
        ),
      align: "center",
      responsive: ["lg"],
      width: "8%",
    },
    {
      title: "Cameras",
      key: "hasCameras",
      dataIndex: "hasCameras",
      render: (value, row) =>
        renderEnableRow(
          row,
          value,
          <SummaryCardCamera accountId={row.accountId} />
        ),
      align: "center",
      responsive: ["lg"],
      width: "8%",
    },
    {
      title: "Created Date",
      render: (value, row) =>
        getAccountTimeFormatDate(
          row.createdAt || "",
          selectedAccount,
          true,
          false
        ),
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(accountQueryInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
      width: isBigScreen ? "10%" : 110,
      responsive: ["xl"],
    },
    // 마지막 버튼
    {
      title: "",
      render: (value, row, index) => (
        <s.Row className="table-button-wrapper">
          {row.enableEdit && (
            <RBACWrapper requiredPermissions="manageAccount">
              <TableButton
                label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  onModal("edit", row);
                }}
                data-testid={`account-edit-${index}`}
              />
            </RBACWrapper>
          )}

          {checkIsSuspended(row) !== SUSPENSION_STATUS.DISABLED && (
            <AccountAccessButton accountId={row.accountId} index={index} />
          )}
        </s.Row>
      ),

      align: "right",
      width: isMobile ? 50 : 180,
    },
  ];

  const columnTitle = () => {
    const size = 18;
    if (selectedAccount.accountLevel === LEVEL_TYPE.CP) {
      return (
        <button
          className="expand-collapse-icon"
          onClick={onToggleExpandAllInPage}
          disabled={expandableRowKeysInPage.length === 0}
          data-testid={`account-toggle-expand-all`}
        >
          {isAllExpandedInPage ? (
            <BsArrowsCollapse size={size} />
          ) : (
            <MdOutlineExpand size={size} />
          )}
        </button>
      );
    }
    return <></>;
  };

  const expandableProps = useMemo<ExpandableConfig<AccountInfo>>(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys,
      onExpand: (expanded: boolean, record: AccountInfo) => {
        const row = record as AccountInfo & { key?: string };
        const recordRowKey = String(row.key ?? row.accountId);

        setExpandedRowKeys((prev) => {
          if (expanded) {
            return [...prev, recordRowKey];
          }
          return prev.filter((key) => key !== recordRowKey);
        });
      },
      expandedRowRender: (record: AccountInfo) => (
        <AccountExpandContainer
          selectedData={record}
          columns={columns as TableColumnsType<AccountInfo>}
          queryInfo={accountQueryInfo}
          expandable={isRowExpandable(record) ? expandableProps : undefined}
        />
      ),

      rowExpandable: (record: AccountInfo) => isRowExpandable(record),
      columnTitle:
        selectedAccount.parentLevel === LEVEL_TYPE.CP
          ? undefined
          : columnTitle(),
    };
  }, [expandedRowKeys, columns, accountQueryInfo, selectedAccount.parentLevel]);

  return (
    <AccountPresenter
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      columns={columns}
      data={accountList}
      onClickSearch={onClickSearch}
      paginationPerPage={accountQueryInfo.pageLimit}
      queryInfo={accountQueryInfo}
      keyword={accountQueryInfo.keyword as string}
      onChangePage={onChangePage}
      status={status}
      initialPending={isLoading}
      onSuccessCreateAccount={onSuccessCreateAccount}
      expandableProps={expandableProps}
      AccountAccessModal={AccountAccessModal}
    />
  );
}
